import { useState, useEffect, useRef, useCallback } from 'react';
import ApiService from '../services/SupabaseApiService';
import { supabase } from '../services/supabase';

const usePresence = (userType, userId, familyId, userName = '') => {
  const [onlineMembers, setOnlineMembers] = useState([]);
  const [isOnline, setIsOnline] = useState(false);
  const [isAvailableForCalls, setIsAvailableForCalls] = useState(true);
  const heartbeatInterval = useRef(null);
  const presenceSubscription = useRef(null);

  // Device info for presence tracking
  const getDeviceInfo = useCallback(() => {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      lastActivity: new Date().toISOString()
    };
  }, []);

  // Update presence status
  const updatePresenceStatus = useCallback(async (online = true, availableForCalls = true) => {
    if (!userType || !userId || !familyId) {
      console.warn('⚠️ updatePresenceStatus called but missing parameters:', {
        hasUserType: !!userType,
        hasUserId: !!userId,
        hasFamilyId: !!familyId
      });
      return;
    }

    try {
      // A parent's presence identity must match the LIVE auth session. If a
      // different account signed in (same browser, another tab), this tab can
      // no longer receive calls for this user — keeping them "online" would
      // lure family members into calls that ring forever. Go offline instead.
      if (userType === 'parent') {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user || session.user.id !== userId) {
          console.warn('⚠️ Auth session no longer belongs to this user, marking offline instead of heartbeating:', {
            presenceUserId: userId?.substring(0, 8),
            sessionUserId: session?.user?.id?.substring(0, 8) || 'none'
          });
          await ApiService.markUserOffline(userId);
          setIsOnline(false);
          return;
        }
      }

      console.log('📡 Updating presence status:', {
        online,
        availableForCalls,
        userType,
        userId: userId.substring(0, 20) + '...'
      });
      
      const deviceInfo = getDeviceInfo();
      const result = await ApiService.updatePresence(
        userType, 
        userId, 
        userName, // real display name so others can see WHO is online
        online, 
        availableForCalls, 
        '', // statusMessage
        'active', // currentActivity
        deviceInfo
      );
      
      console.log('✅ Presence update result:', result);
      
      if (result.success) {
        setIsOnline(online);
        setIsAvailableForCalls(availableForCalls);
        console.log('🟢 Presence status updated successfully');
      } else {
        console.error('❌ Presence update failed:', result.error);
      }
    } catch (error) {
      console.error('❌ Failed to update presence:', error);
    }
  }, [userType, userId, familyId, userName, getDeviceInfo]);

  // Mark user as offline
  const goOffline = useCallback(async () => {
    if (!userType || !userId) return;

    try {
              await ApiService.markUserOffline(userId);
      setIsOnline(false);
    } catch (error) {
      console.error('Failed to mark user offline:', error);
    }
  }, [userType, userId]);

  // Load family presence data
  const loadFamilyPresence = useCallback(async () => {
    console.log('👥 Loading family presence for familyId:', familyId);
    try {
      const presence = await ApiService.getFamilyPresence(familyId);
      console.log('👥 Family presence loaded:', presence?.length || 0, 'members');
      setOnlineMembers(presence || []);
    } catch (error) {
      console.error('❌ Failed to load family presence:', error);
    }
  }, [familyId]);

  // Start presence tracking
  const startPresenceTracking = useCallback(() => {
    console.log('📞 startPresenceTracking called with:', {
      userType,
      userId: userId ? userId.substring(0, 20) + '...' : null,
      familyId: familyId ? familyId.substring(0, 20) + '...' : null
    });
    
    if (!userType || !userId || !familyId) {
      console.error('❌ Missing presence parameters, cannot start tracking');
      return;
    }

    console.log('✅ All parameters present, starting presence tracking...');
    // Mark as online initially
    updatePresenceStatus(true, isAvailableForCalls);

    // Set up heartbeat (every 30 seconds)
    heartbeatInterval.current = setInterval(() => {
      updatePresenceStatus(true, isAvailableForCalls);
    }, 30000);

    // Subscribe to family presence changes
    console.log('📡 Subscribing to presence changes...');
    presenceSubscription.current = ApiService.subscribeToPresenceChanges(
      familyId,
      (payload) => {
        console.log('🔔 Presence change detected:', payload);
        // Refresh online members list
        loadFamilyPresence();
      }
    );

    // Load initial presence data
    console.log('📥 Loading initial presence data...');
    loadFamilyPresence();
    
    console.log('✅ Presence tracking fully initialized!');
  }, [userType, userId, familyId, isAvailableForCalls, updatePresenceStatus, loadFamilyPresence]);

  // Stop presence tracking
  const stopPresenceTracking = useCallback(() => {
    // Clear heartbeat
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
    }

    // Unsubscribe from presence changes
    if (presenceSubscription.current) {
      presenceSubscription.current.unsubscribe();
      presenceSubscription.current = null;
    }

    // Mark as offline
    goOffline();
  }, [goOffline]);

  // Toggle availability for calls
  const toggleAvailability = useCallback((available) => {
    setIsAvailableForCalls(available);
    updatePresenceStatus(isOnline, available);
  }, [isOnline, updatePresenceStatus]);

  // Initialize presence tracking
  useEffect(() => {
    console.log('🔍 usePresence hook effect triggered:', {
      userType,
      userId: userId ? userId.substring(0, 20) + '...' : null,
      familyId: familyId ? familyId.substring(0, 20) + '...' : null,
      hasAll: !!(userType && userId && familyId)
    });
    
    if (userType && userId && familyId) {
      console.log('🔄 Starting presence tracking...');
      startPresenceTracking();

      // Handle page visibility changes
      const handleVisibilityChange = () => {
        if (document.hidden) {
          // Page is hidden - reduce heartbeat frequency
          if (heartbeatInterval.current) {
            clearInterval(heartbeatInterval.current);
            heartbeatInterval.current = setInterval(() => {
              updatePresenceStatus(true, isAvailableForCalls);
            }, 60000); // Every minute when hidden
          }
        } else {
          // Page is visible - restore normal heartbeat
          if (heartbeatInterval.current) {
            clearInterval(heartbeatInterval.current);
            heartbeatInterval.current = setInterval(() => {
              updatePresenceStatus(true, isAvailableForCalls);
            }, 30000); // Every 30 seconds when visible
          }
          updatePresenceStatus(true, isAvailableForCalls);
        }
      };

      // Handle beforeunload (page closing)
      const handleBeforeUnload = () => {
        goOffline();
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        stopPresenceTracking();
      };
    }
  }, [userType, userId, familyId, startPresenceTracking, stopPresenceTracking, isAvailableForCalls, updatePresenceStatus, goOffline]);

  return {
    onlineMembers,
    isOnline,
    isAvailableForCalls,
    toggleAvailability,
    refreshPresence: loadFamilyPresence
  };
};

export default usePresence; 